import {
  ContainerStatusEntity,
  ContainerTotalFillEntity,
  selectContainerStatuses,
  transportCategory,
  TransportCategory
} from "@ots-frontend/ots-data";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {AvailableLoadsBy, useGetContainerStatuses} from "@ots-frontend/ots-ui";

export type UseGetContainerStatusInsightsProps = {
  category?: TransportCategory
}

export type UseGetContainerStatusInsightsResult = {
  availableLoads: number,
  availableLoadsBy: AvailableLoadsBy
  groupBy: {
    potentialShutIns: ContainerStatusEntity[]
    warningHaulable: ContainerStatusEntity[]
    critical: ContainerStatusEntity[]
    highNonPrimaryVolume: ContainerStatusEntity[]
  }
  isLoading: boolean
  isFetching: boolean
  refetch: any
}

export const useGetContainerStatusInsights = (props: UseGetContainerStatusInsightsProps = {}): UseGetContainerStatusInsightsResult => {
  const {category} = props

  const {t} = useTranslation(["common"])

  const {
    containerStatuses,
    isLoading,
    isFetching,
    refetch
  } = useGetContainerStatuses()

  const availableLoads = useMemo(() => {
    return containerStatuses
      .map((s) => s.meterFill)
      .filter((fill): fill is ContainerTotalFillEntity => !!fill)
      .map((fill) => fill.availableLoadCount ?? 0)
      .reduce((total, loadCount) => total + loadCount, 0)
  }, [containerStatuses])

  const availableLoadsByAssignedCompany = useMemo(() => {
    const record: Record<string, number> = {}

    for (const status of containerStatuses) {
      if (status.meterFill?.availableLoadCount) {
        const companyName = status.assignedCompanyName ?? t("common.shared.unassigned", "Unassigned") ?? "Unassigned"
        if (!(companyName in record)) {
          record[companyName] = 0
        }

        record[companyName] = record[companyName] + status.meterFill.availableLoadCount
      }
    }

    return record
  }, [containerStatuses, t])

  const availableLoadsByRoute = useMemo(() => {
    const record: Record<string, number> = {}

    for (const status of containerStatuses) {
      if (status.meterFill?.availableLoadCount) {
        const routeName = status.siteRouteName ?? t("common.entity.route.none", "(No Route)") ?? "(No Route)"
        if (!(routeName in record)) {
          record[routeName] = 0
        }

        record[routeName] = record[routeName] + status.meterFill.availableLoadCount
      }
    }

    return record
  }, [containerStatuses, t])

  const critical = useMemo(() => {
    return selectContainerStatuses(containerStatuses, {criticalStatusOnly: true})
  }, [containerStatuses])

  const highNonPrimaryVolume = useMemo(() => {
    return selectContainerStatuses(containerStatuses, {highNonPrimaryVolumeOnly: true})
  }, [containerStatuses])

  const warningHaulable = useMemo(() => {
    return selectContainerStatuses(containerStatuses, {
      minimumMeterHeight: 156,
      transportViaPipeline: category === transportCategory.water ? false : undefined,
      transportOilViaPipeline: category === transportCategory.oil ? false : undefined,
    })
  }, [containerStatuses, category])

  const potentialShutIns = useMemo(() => {
    return containerStatuses.filter((s) => s.meterFill?.actualPotentialShutIn)
  }, [containerStatuses])

  return {
    availableLoads: availableLoads,
    availableLoadsBy: {
      company: availableLoadsByAssignedCompany,
      route: availableLoadsByRoute,
    },
    groupBy: {
      warningHaulable: warningHaulable,
      critical: critical,
      highNonPrimaryVolume: highNonPrimaryVolume,
      potentialShutIns: potentialShutIns,
    },
    isLoading: isLoading,
    isFetching: isFetching,
    refetch: refetch,
  }
}
